import { ProductFeature, Sangha, SanghaType } from "@app/shared/types";
import {
    Avatar,
    Card,
    CardActionArea,
    CardActions,
    CardContent,
    CardMedia,
    Chip,
    Typography,
    alpha,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { SanghaDayAndTimeDescription } from "features/sangha/SanghaDayAndTimeDescription";
import { useUserTimezone } from "hooks/useUserTimezone";
import { analyticsTrack } from "app/analytics/track";
import { routes } from "app/routes";
import { useHistory, useLocation } from "react-router";
import { cloudinary } from "app/cloudinary";
import { AdvancedImage, responsive } from "@cloudinary/react";
import { externalImageSrc } from "app/images";
import GroupIcon from "@mui/icons-material/Group";
import {
    getCurrentCohort,
    isUserInFreeTrialSession,
    isUserInSetupFlow,
    isUserOutsideOfMemberZone,
} from "features/signup/signupHelpers";
import { isSet } from "lodash";
import { useSelector } from "react-redux";
import { selectUser } from "features/auth/auth";

const useStyles = makeStyles((theme) => ({
    card: {
        backgroundColor: theme.palette.coral100,
        color: theme.palette.grey700,
        borderRadius: theme.borderRadius.default,
        boxShadow: theme.shadow.default,

        "&:hover": {
            boxShadow: theme.shadow.spread,
        },
    },
    actionArea: {
        display: "flex",
        flexDirection: "column",
        height: "100%",
    },
    cardContent: {
        paddingTop: theme.spacing(1),
        paddingLeft: theme.spacing(3),
        paddingRight: theme.spacing(3),
        display: "flex",
        flexDirection: "column",
        flex: "1 0 auto",
        boxSizing: "border-box",
        width: "100%",
    },
    cardImage: {
        height: theme.spacing(20),
        width: "100%",
        objectFit: "cover",
    },
    freeTrialLabel: {
        position: "absolute",
        top: 10,
        right: 10,
    },
    eventTime: {
        marginBottom: 0,
    },
    eventName: {
        marginBottom: theme.spacing(0),
        marginTop: theme.spacing(1),
    },
    subtitle: {
        marginBottom: 0,
    },
    cardActions: {
        padding: 0,
        marginTop: theme.spacing(2),
        maxWidth: "100%",
        display: "flex",
        justifyContent: "space-between",
    },
    avatar: {
        width: theme.spacing(4),
        height: theme.spacing(4),
        marginRight: theme.spacing(1),
    },
    teacherName: {
        overflow: "hidden",
        textOverflow: "ellipsis",
        marginBottom: 0,
    },
    groupIcon: {
        backgroundColor: alpha(theme.palette.grey700, 0.2),
        borderRadius: theme.borderRadius.circular,
        width: theme.spacing(4),
        height: theme.spacing(4),
        marginLeft: theme.spacing(1),
        padding: theme.spacing(0.5),
        marginRight: theme.spacing(1),
    },
    memberCountContainer: {
        display: "flex",
        alignItems: "center",
        whiteSpace: "nowrap",
    },
    memberCount: {
        marginBottom: 0,
    },
    bottomContainer: {
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        flex: "1 0 auto",
        boxSizing: "border-box",
        width: "100%",
    },
    teacherContainer: {
        display: "flex",
        alignItems: "center",
        flex: "1 0 auto",
        maxWidth: "55%",
        [theme.breakpoints.down("md")]: {
            maxWidth: "50%",
        },
    },
}));

export const InterestGroupCard = (props: { interestGroup: Sangha }): JSX.Element => {
    const classes = useStyles();
    const { interestGroup } = props;
    const timezone = useUserTimezone();
    const history = useHistory();
    const groupIsFull = !interestGroup.availableSpots || interestGroup.availableSpots <= 0;

    const location = useLocation();
    const isSetupFlow = isUserInSetupFlow(location);
    const isOutsideOfMemberZone = isUserOutsideOfMemberZone(location);

    const user = useSelector(selectUser);
    const isActiveMember = user?.features.includes(ProductFeature.CORE_MEMBERSHIP);

    const isFreeTrialSignupFlow = isUserInFreeTrialSession() && (!isActiveMember || isSetupFlow);

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const handleClick = (event?: any) => {
        if (!groupIsFull) {
            analyticsTrack("Button Clicked", {
                label: "Join",
                id: interestGroup.id,
                type: SanghaType.InterestGroup,
                location: "InterestGroupCard",
                isFreeTrial: isFreeTrialSignupFlow,
                cohort: getCurrentCohort(),
            });
            // hacks to prevent navigation on windows/macOS if ctrl or meta(macOS)
            // keys are pressed in combination with a click and allows browser to
            // fulfill its default function
            if (event.metaKey || event.ctrlKey) {
                return;
            }

            if (isSetupFlow) {
                history.push(routes.setupAddInterestGroup(interestGroup.id));
            } else if (isOutsideOfMemberZone) {
                history.push(routes.addInterestGroup(interestGroup.id));
            } else {
                history.push(routes.memberAddInterestGroup(interestGroup.id));
            }
        }
    };

    const cldImage = cloudinary.image(interestGroup.topicData?.imageId).format("auto");

    const numberOfMembers = interestGroup.maxSpots
        ? interestGroup.maxSpots - interestGroup.availableSpots
        : undefined;

    return (
        <Card className={classes.card} data-testid="interestGroupCard" raised={true}>
            <CardActionArea
                onClick={handleClick}
                disabled={groupIsFull}
                className={classes.actionArea}
            >
                <CardMedia
                    component={() => {
                        return (
                            <AdvancedImage
                                className={classes.cardImage}
                                cldImg={cldImage}
                                plugins={[responsive({ steps: 200 })]}
                            />
                        );
                    }}
                />
                {isFreeTrialSignupFlow && (
                    <Chip
                        variant="darkGrey"
                        label="Try it for free"
                        className={classes.freeTrialLabel}
                        size="small"
                    />
                )}
                <CardContent className={classes.cardContent}>
                    <Typography variant="caption" className={classes.eventTime}>
                        <SanghaDayAndTimeDescription
                            sangha={interestGroup}
                            userTimeZone={timezone}
                            renderTimeZone={true}
                        />
                    </Typography>
                    <Typography variant="h5" className={classes.eventName}>
                        {interestGroup.name}
                    </Typography>
                    <div className={classes.bottomContainer}>
                        <Typography variant="body1" className={classes.subtitle}>
                            {interestGroup.presentationData?.subtitle}
                        </Typography>
                        <CardActions className={classes.cardActions}>
                            <div className={classes.teacherContainer}>
                                <Avatar
                                    data-testid="teacherAvatar"
                                    className={classes.avatar}
                                    alt={interestGroup.teacher.name}
                                    src={
                                        interestGroup.teacher.picture
                                            ? externalImageSrc(interestGroup.teacher.picture, 32)
                                            : "EMPTY"
                                    }
                                    variant="circular"
                                />
                                <Typography variant="caption" className={classes.teacherName}>
                                    <strong>With {interestGroup.teacher.name}</strong>
                                </Typography>
                            </div>
                            {numberOfMembers ? (
                                <div className={classes.memberCountContainer}>
                                    <GroupIcon className={classes.groupIcon} />
                                    <Typography variant="caption" className={classes.memberCount}>
                                        {`${numberOfMembers} ${
                                            numberOfMembers > 1 ? "Members" : "Member"
                                        }`}
                                    </Typography>
                                </div>
                            ) : null}
                        </CardActions>
                    </div>
                </CardContent>
            </CardActionArea>
        </Card>
    );
};

export default InterestGroupCard;
